// combatpro/runtime/CombatOpsPanel.js
//
// Combat PRO - Combat Ops panel (per-combatant live runtime UI).
//
// Surfaces the DM Operations per-combatant runtime for the ACTIVE monster on the
// Selection page (the approved owner for per-combatant outcome / action economy /
// tactical state): action economy, combat outcome, tactical cover / visibility /
// side / group / zone, Legendary Resistance, limited-use / recharge resources, temp
// HP and death saves. Reads and writes the runtime through CombatContext.setMonsterRt
// using the pure combatRuntime operations, so nothing here owns state and the
// canonical monster record is never mutated. No optional chaining, no nullish
// coalescing, no em dash.

import React, { useContext } from 'react';
import { CombatContext } from '../contexts/CombatContext';
import { familyArrayKey, activeFamilyOf } from '../encounter/encounterStoreEngine';
import {
  getRt, setEconomy, resetTurn, setOutcome, setTactical, setSide, setGroup,
  setLegendaryResistance, spendLegendaryResistance, restoreLegendaryResistance, legendaryResistanceRemaining,
  addResource, updateResource, removeResource, spendResource, restoreResource, applyRechargeRoll,
  recordDeathSave, resetDeathSaves, setTempHP, setReady, clearReady, READY_STATES, READY_STATE_LABELS,
  OUTCOMES, OUTCOME_LABELS, SIDES, SIDE_LABELS, COVERS, COVER_LABELS, VISIBILITIES, VISIBILITY_LABELS,
  RESOURCE_KINDS, RESOURCE_KIND_LABELS, newRtId
} from './combatRuntime';

function nz(v) { return (v === null || typeof v === 'undefined') ? '' : v; }

function ResourceRow(props) {
  const r = props.resource;
  return (
    <div className="vi-co-res">
      <input className="vi-tr-input vi-eb-grow" placeholder="Ability / resource name" value={nz(r.name)} onChange={function (e) { props.onUpdate(r.id, { name: e.target.value }); }} />
      <span className="vi-tr-status vi-eb-kind">{RESOURCE_KIND_LABELS[r.kind] || r.kind}</span>
      <span className="vi-co-res-count">{r.current} / {r.max}</span>
      <button type="button" className="vi-tr-mini" title="Spend one" onClick={function () { props.onSpend(r.id); }}>{'−'}</button>
      <button type="button" className="vi-tr-mini" title="Restore one" onClick={function () { props.onRestore(r.id); }}>{'+'}</button>
      {r.kind === 'recharge' ? (
        <button type="button" className="vi-tr-btn" title={'Recharge ' + r.rechargeMin + '-' + r.rechargeMax} onClick={function () { props.onRecharge(r.id); }}>Roll recharge</button>
      ) : null}
      <input className="vi-tr-input vi-tr-input-sm" type="number" min="0" title="Max" value={nz(r.max)} onChange={function (e) { props.onUpdate(r.id, { max: e.target.value === '' ? 0 : parseInt(e.target.value, 10) }); }} />
      <button type="button" className="vi-tr-mini vi-tr-mini-danger" title="Remove" onClick={function () { props.onRemove(r.id); }}>{'✖'}</button>
    </div>
  );
}

function CombatOpsPanel() {
  const ctx = useContext(CombatContext);
  const state = ctx.state;
  const index = state.currentCombatantIndex;
  const monster = Array.isArray(state.monsters) ? state.monsters[index] : null;

  const populated = monster && ((monster.customName && String(monster.customName).trim()) || (monster.selectedMonster && String(monster.selectedMonster).trim()));
  if (!populated) {
    return (
      <div className="vi-co-panel">
        <div className="vi-tr-panel-title">Combat Ops</div>
        <div className="vi-tr-empty">Select an Active Monster above to manage its live combat runtime: action economy, outcome, tactical state, Legendary Resistance, limited-use resources and death saves.</div>
      </div>
    );
  }

  const rt = getRt(monster);
  const name = (monster.customName && monster.customName.trim()) ? monster.customName.trim() : (monster.selectedMonster || ('Monster ' + (index + 1)));
  const store = state.encounterStore;
  const ai = (store && typeof store.activeIndex === 'number') ? store.activeIndex : 0;
  const enc = (store && Array.isArray(store.encounters)) ? (store[familyArrayKey(activeFamilyOf(store))] || store.encounters)[ai] : null;
  const groups = (enc && enc.dmOps && Array.isArray(enc.dmOps.groups)) ? enc.dmOps.groups : [];
  const zones = (enc && enc.encounterBrief && enc.encounterBrief.setup && Array.isArray(enc.encounterBrief.setup.zones)) ? enc.encounterBrief.setup.zones : [];

  function apply(fn) { ctx.setMonsterRt(index, fn); }

  return (
    <div className="vi-co-panel">
      <div className="vi-co-head">
        <div className="vi-co-title">Combat Ops {'·'} <span className="vi-co-name">{name}</span></div>
        <span className={'vi-tr-status vi-co-outcome out-' + rt.outcome}>{OUTCOME_LABELS[rt.outcome]}</span>
      </div>

      <div className="vi-co-grid">
        <div className="vi-co-block">
          <div className="vi-co-block-title">Action Economy</div>
          <div className="vi-eb-chiprow">
            <button type="button" className={'vi-tr-chip' + (rt.action ? ' is-on' : '')} onClick={function () { apply(function (r) { return setEconomy(r, { action: !r.action }); }); }}>Action</button>
            <button type="button" className={'vi-tr-chip' + (rt.bonus ? ' is-on' : '')} onClick={function () { apply(function (r) { return setEconomy(r, { bonus: !r.bonus }); }); }}>Bonus</button>
            <button type="button" className={'vi-tr-chip' + (rt.reaction ? ' is-on' : '')} onClick={function () { apply(function (r) { return setEconomy(r, { reaction: !r.reaction }); }); }}>Reaction</button>
          </div>
          <div className="vi-eb-chiprow">
            <span className="vi-eb-mini-label">Move</span>
            <button type="button" className="vi-tr-mini" onClick={function () { apply(function (r) { return setEconomy(r, { moveUsed: Math.max(0, r.moveUsed - 5) }); }); }}>{'−'}5</button>
            <span className="vi-co-move">{rt.moveUsed} / {rt.moveMax} ft</span>
            <button type="button" className="vi-tr-mini" onClick={function () { apply(function (r) { return setEconomy(r, { moveUsed: r.moveUsed + 5 }); }); }}>+5</button>
            <input className="vi-tr-input vi-tr-input-sm" type="number" min="0" title="Total movement" value={nz(rt.moveMax)} onChange={function (e) { apply(function (r) { return setEconomy(r, { moveMax: e.target.value === '' ? 0 : parseInt(e.target.value, 10) }); }); }} />
            <button type="button" className="vi-tr-btn" onClick={function () { apply(resetTurn); }}>Reset turn</button>
          </div>
        </div>

        <div className="vi-co-block">
          <div className="vi-co-block-title">Outcome</div>
          <div className="vi-eb-chiprow">
            {OUTCOMES.map(function (o) { return <button key={o} type="button" className={'vi-tr-chip' + (rt.outcome === o ? ' is-on' : '')} onClick={function () { apply(function (r) { return setOutcome(r, o); }); }}>{OUTCOME_LABELS[o]}</button>; })}
          </div>
        </div>

        <div className="vi-co-block">
          <div className="vi-co-block-title">Tactical</div>
          <div className="vi-eb-chiprow"><span className="vi-eb-mini-label">Cover</span>
            {COVERS.map(function (c) { return <button key={c} type="button" className={'vi-tr-chip' + (rt.cover === c ? ' is-on' : '')} onClick={function () { apply(function (r) { return setTactical(r, { cover: c }); }); }}>{COVER_LABELS[c]}</button>; })}
          </div>
          <div className="vi-eb-chiprow"><span className="vi-eb-mini-label">Visibility</span>
            {VISIBILITIES.map(function (v) { return <button key={v} type="button" className={'vi-tr-chip' + (rt.visibility === v ? ' is-on' : '')} onClick={function () { apply(function (r) { return setTactical(r, { visibility: v }); }); }}>{VISIBILITY_LABELS[v]}</button>; })}
            <button type="button" className={'vi-tr-chip' + (rt.elevated ? ' is-on' : '')} onClick={function () { apply(function (r) { return setTactical(r, { elevated: !r.elevated }); }); }}>Elevated</button>
          </div>
          <div className="vi-eb-chiprow"><span className="vi-eb-mini-label">Side</span>
            {SIDES.map(function (sd) { return <button key={sd} type="button" className={'vi-tr-chip' + (rt.side === sd ? ' is-on' : '')} onClick={function () { apply(function (r) { return setSide(r, rt.side === sd ? null : sd); }); }}>{SIDE_LABELS[sd]}</button>; })}
          </div>
          <div className="vi-eb-chiprow">
            <span className="vi-eb-mini-label">Group</span>
            <select className="vi-tr-input" value={nz(rt.group)} onChange={function (e) { apply(function (r) { return setGroup(r, e.target.value); }); }}>
              <option value="">None</option>
              {groups.map(function (g) { return <option key={g.id} value={g.id}>{g.name || 'Group'}</option>; })}
            </select>
            <span className="vi-eb-mini-label">Zone</span>
            <select className="vi-tr-input" value={nz(rt.zone)} onChange={function (e) { apply(function (r) { return setTactical(r, { zone: e.target.value }); }); }}>
              <option value="">None</option>
              {zones.map(function (z) { return <option key={z.id} value={z.id}>{z.name || 'Zone'}</option>; })}
            </select>
          </div>
        </div>

        <div className="vi-co-block">
          <div className="vi-co-block-title">Legendary Resistance</div>
          <div className="vi-eb-chiprow">
            <span className="vi-eb-mini-label">Max</span>
            <input className="vi-tr-input vi-tr-input-sm" type="number" min="0" value={nz(rt.legendaryResistance.max)} onChange={function (e) { apply(function (r) { return setLegendaryResistance(r, { max: e.target.value === '' ? 0 : parseInt(e.target.value, 10) }); }); }} />
            <span className="vi-co-lr">{legendaryResistanceRemaining(rt)} / {rt.legendaryResistance.max} remaining</span>
            <button type="button" className="vi-tr-btn" disabled={legendaryResistanceRemaining(rt) <= 0} onClick={function () { apply(spendLegendaryResistance); }}>Use</button>
            <button type="button" className="vi-tr-btn" onClick={function () { apply(restoreLegendaryResistance); }}>Restore</button>
          </div>
        </div>

        <div className="vi-co-block vi-co-block-wide">
          <div className="vi-co-block-title">Resources</div>
          {(rt.resources || []).length ? rt.resources.map(function (res) {
            return <ResourceRow key={res.id} resource={res}
              onUpdate={function (id, patch) { apply(function (r) { return updateResource(r, id, patch); }); }}
              onSpend={function (id) { apply(function (r) { return spendResource(r, id); }); }}
              onRestore={function (id) { apply(function (r) { return restoreResource(r, id); }); }}
              onRecharge={function (id) { var roll = Math.floor(Math.random() * 6) + 1; apply(function (r) { return applyRechargeRoll(r, id, roll).rt; }); }}
              onRemove={function (id) { apply(function (r) { return removeResource(r, id); }); }} />;
          }) : <div className="vi-tr-empty">No limited-use resources. Add recharge abilities, X/day spells, charges or pools.</div>}
          <div className="vi-eb-chiprow">
            <span className="vi-eb-mini-label">Add</span>
            {RESOURCE_KINDS.map(function (k) { return <button key={k} type="button" className="vi-tr-chip" onClick={function () { apply(function (r) { return addResource(r, { name: '', kind: k, max: 1, current: 1, rechargeMin: (k === 'recharge' ? 5 : 6), rechargeMax: 6, id: newRtId('res') }); }); }}>{'+ ' + RESOURCE_KIND_LABELS[k]}</button>; })}
          </div>
        </div>

        <div className="vi-co-block">
          <div className="vi-co-block-title">Temp HP</div>
          <div className="vi-eb-chiprow">
            <input className="vi-tr-input vi-tr-input-sm" type="number" min="0" value={nz(rt.tempHP)} onChange={function (e) { apply(function (r) { return setTempHP(r, e.target.value === '' ? 0 : parseInt(e.target.value, 10)); }); }} />
            <span className="vi-eb-mini-label">temporary hit points</span>
          </div>
        </div>

        <div className="vi-co-block vi-co-block-wide">
          <div className="vi-co-block-title">Ready / Held Action</div>
          <div className="vi-eb-item-row">
            <input className="vi-tr-input vi-eb-grow" placeholder="Readied action (reference an ability, weapon or spell)" value={nz(rt.ready.action)} onChange={function (e) { apply(function (r) { return setReady(r, { action: e.target.value }); }); }} />
            <input className="vi-tr-input vi-eb-grow" placeholder="Trigger (e.g. when an enemy enters the doorway)" value={nz(rt.ready.trigger)} onChange={function (e) { apply(function (r) { return setReady(r, { trigger: e.target.value }); }); }} />
          </div>
          <div className="vi-eb-chiprow">
            <button type="button" className={'vi-tr-chip' + (rt.ready.reaction ? ' is-on' : '')} onClick={function () { apply(function (r) { return setReady(r, { reaction: !r.ready.reaction }); }); }}>Uses Reaction</button>
            {READY_STATES.map(function (st) { return <button key={st} type="button" className={'vi-tr-chip' + (rt.ready.status === st ? ' is-on' : '')} onClick={function () { apply(function (r) { return setReady(r, { status: st }); }); }}>{READY_STATE_LABELS[st]}</button>; })}
            <button type="button" className="vi-tr-btn" onClick={function () { apply(clearReady); }}>Clear</button>
          </div>
        </div>

        <div className="vi-co-block">
          <div className="vi-co-block-title">Death Saves</div>
          <div className="vi-eb-chiprow">
            <span className="vi-co-ds succ">Successes {rt.deathSaves.successes} / 3</span>
            <button type="button" className="vi-tr-btn" onClick={function () { apply(function (r) { return recordDeathSave(r, 'success'); }); }}>+ Success</button>
            <span className="vi-co-ds fail">Failures {rt.deathSaves.failures} / 3</span>
            <button type="button" className="vi-tr-btn vi-tr-btn-danger" onClick={function () { apply(function (r) { return recordDeathSave(r, 'failure'); }); }}>+ Failure</button>
            <button type="button" className="vi-tr-btn" onClick={function () { apply(resetDeathSaves); }}>Reset</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CombatOpsPanel;
